/**
 * Onboarding client — the ONLY way apps/player talks to services/onboarding
 * (FIRST NIGHT / LOOP 2, contracts packages/contracts/src/onboarding.ts).
 *
 * - Base URL: VITE_ONBOARDING_URL (default http://localhost:8792).
 * - AUTH (D-010): every call carries `Authorization: Bearer <supabase_jwt>` —
 *   the D-009 token off the stored auth session (platform auth.getSession()).
 *   No token ⇒ OnboardingApiError("not_authenticated") BEFORE any network I/O.
 * - Every request body AND every response body is zod-validated against
 *   @matchday/contracts at the boundary — a drifted server payload throws
 *   OnboardingApiError("bad_response") at the call site, never lands in the UI.
 * - Server errors (contracts ApiError shape) surface as OnboardingApiError with
 *   the documented code (`gamertag_taken`, `insufficient_coins`, …).
 *
 * ── EVENTS ────────────────────────────────────────────────────────────────
 * The onboarding domain events (onboarding_started, badge_selected,
 * avatar_purchased, gamertag_set, ready_earned, coins_earned/spent,
 * confidence_changed) are emitted SERVER-SIDE by the service on each write.
 * This client emits NOTHING — the route rides ui-level screen_viewed (platform)
 * and ceremony_played (juice) only. Never double-emit a server event here.
 *
 * Path map (services/onboarding surface):
 *   GET  /onboarding/catalogue        → IdentityCatalogue
 *   GET  /onboarding/state            → OnboardingStateResponse
 *   POST /onboarding/badge            → PickBadgeRequest → OnboardingStateResponse
 *   POST /onboarding/grant            → ClaimGrantRequest → OnboardingStateResponse
 *   POST /onboarding/identity         → ClaimIdentityRequest → OnboardingStateResponse
 *   GET  /onboarding/gamertag-check?tag= → GamertagCheck
 *   POST /onboarding/avatar           → {avatar_id} → OnboardingStateResponse  (CCR — see below)
 *
 * CONTRACT-CHANGE REQUESTS FILED:
 *   1. No badge-only setter distinct from S2 pickBadge — the Locker Room's
 *      "change my colours" reuses POST /onboarding/badge with
 *      from_lucky_chest=false (setBadge below). A dedicated PUT /onboarding/badge
 *      that never re-rolls a lucky rare would be cleaner.
 *   2. No avatar-equip endpoint in packages/contracts/src/onboarding.ts —
 *      claimIdentity debits coins + sets the gamertag, so it cannot re-equip an
 *      already-owned avatar. setAvatar() below rides POST /onboarding/avatar with
 *      a locally-defined {avatar_id} request until SetAvatarRequest is ratified.
 */
import {
  ApiError,
  ClaimGrantRequest,
  ClaimIdentityRequest,
  GamertagCheck,
  IdentityCatalogue,
  OnboardingStateResponse,
  PickBadgeRequest,
} from "@matchday/contracts";
import { z } from "zod";
import { auth } from "./platform";

export const ONBOARDING_URL: string =
  (import.meta.env?.VITE_ONBOARDING_URL as string | undefined) ?? "http://localhost:8792";

export class OnboardingApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly detail?: unknown,
    public readonly retryAfterS?: number,
  ) {
    super(`onboarding api error ${status}: ${code}`);
    this.name = "OnboardingApiError";
  }
}

/** CCR #2: the equip-avatar request the Locker Room needs (no coin debit). */
const SetAvatarRequest = z.object({ avatar_id: z.string() });

export interface OnboardingApiOptions {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  /** The D-009 bearer source. Default: stored session's supabase_jwt. */
  getToken?: () => Promise<string | null>;
}

export interface OnboardingApi {
  /** GET /onboarding/catalogue — badges + avatars + the starter economy config. */
  getCatalogue(): Promise<IdentityCatalogue>;
  /** GET /onboarding/state — the server truth (drives the resume-step machine). */
  getState(): Promise<OnboardingStateResponse>;
  /** POST /onboarding/progress — record the step; "done" ends the flow. */
  recordProgress(step: string): Promise<OnboardingStateResponse>;
  /** POST /onboarding/badge — S2 commit; from_lucky_chest true for the rare slot. */
  pickBadge(req: PickBadgeRequest): Promise<OnboardingStateResponse>;
  /** POST /onboarding/grant — S4/S7/S10 server-authoritative grants (idempotent). */
  claimGrant(req: ClaimGrantRequest): Promise<OnboardingStateResponse>;
  /** POST /onboarding/identity — S9 buy avatar + set gamertag; flips READY. */
  claimIdentity(req: ClaimIdentityRequest): Promise<OnboardingStateResponse>;
  /** GET /onboarding/gamertag-check?tag= — S9 live inline-dot availability. */
  gamertagCheck(tag: string): Promise<GamertagCheck>;
  /** Locker Room: re-select the equipped badge (reuses POST /onboarding/badge). */
  setBadge(badgeId: string): Promise<OnboardingStateResponse>;
  /** Locker Room: equip an already-owned avatar (CCR — POST /onboarding/avatar). */
  setAvatar(avatarId: string): Promise<OnboardingStateResponse>;
}

/** Default token source: the stored auth session's D-009 supabase_jwt. */
async function sessionToken(): Promise<string | null> {
  const session = await auth.getSession();
  return session?.supabase_jwt ?? null;
}

export function createOnboardingApi(options: OnboardingApiOptions = {}): OnboardingApi {
  const base = (options.baseUrl ?? ONBOARDING_URL).replace(/\/$/, "");
  const fetchImpl = options.fetchImpl ?? fetch;
  const getToken = options.getToken ?? sessionToken;

  async function throwApiError(res: Response): Promise<never> {
    let code = "unknown_error";
    let detail: unknown;
    let retryAfterS: number | undefined;
    try {
      const parsed = ApiError.safeParse(await res.json());
      if (parsed.success) {
        code = parsed.data.error;
        detail = parsed.data.detail;
        retryAfterS = parsed.data.retry_after_s;
      }
    } catch {
      // non-JSON error body — the status + unknown_error is all we have
    }
    throw new OnboardingApiError(res.status, code, detail, retryAfterS);
  }

  async function call<T>(
    method: "GET" | "POST",
    path: string,
    body: unknown,
    // Input typed `unknown` so T infers the OUTPUT (branded) type — branded
    // schemas have unbranded inputs and z.ZodType<T> would otherwise unify
    // T down to the unbranded shape (matchApi.ts precedent).
    schema: z.ZodType<T, z.ZodTypeDef, unknown> | null,
  ): Promise<T> {
    const token = await getToken();
    if (token === null) throw new OnboardingApiError(0, "not_authenticated");
    const headers: Record<string, string> = { authorization: `Bearer ${token}` };
    const init: RequestInit = { method, headers };
    if (body !== undefined) {
      headers["content-type"] = "application/json";
      init.body = JSON.stringify(body);
    }
    let res: Response;
    try {
      res = await fetchImpl(`${base}${path}`, init);
    } catch {
      throw new OnboardingApiError(0, "network_error");
    }
    if (!res.ok) await throwApiError(res);
    if (schema === null) return undefined as T;
    let json: unknown;
    try {
      json = await res.json();
    } catch {
      throw new OnboardingApiError(res.status, "bad_response", "response body was not JSON");
    }
    const parsed = schema.safeParse(json);
    if (!parsed.success) {
      throw new OnboardingApiError(res.status, "bad_response", parsed.error.issues);
    }
    return parsed.data;
  }

  const state = (
    method: "GET" | "POST",
    path: string,
    body: unknown,
  ): Promise<OnboardingStateResponse> => call(method, path, body, OnboardingStateResponse);

  return {
    getCatalogue: () => call("GET", "/onboarding/catalogue", undefined, IdentityCatalogue),
    getState: () => state("GET", "/onboarding/state", undefined),
    recordProgress: (step) =>
      call("POST", "/onboarding/progress", { step }, OnboardingStateResponse),
    pickBadge: (req) => state("POST", "/onboarding/badge", PickBadgeRequest.parse(req)),
    claimGrant: (req) => state("POST", "/onboarding/grant", ClaimGrantRequest.parse(req)),
    claimIdentity: (req) => state("POST", "/onboarding/identity", ClaimIdentityRequest.parse(req)),
    gamertagCheck: (tag) =>
      call("GET", `/onboarding/gamertag-check?tag=${encodeURIComponent(tag)}`, undefined, GamertagCheck),
    setBadge: (badgeId) =>
      state("POST", "/onboarding/badge", PickBadgeRequest.parse({ badge_id: badgeId, from_lucky_chest: false })),
    setAvatar: (avatarId) =>
      state("POST", "/onboarding/avatar", SetAvatarRequest.parse({ avatar_id: avatarId })),
  };
}

/** The app-wide instance (the Locker Room route imports this too — one client). */
export const onboardingApi: OnboardingApi = createOnboardingApi();
